import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// Infrastructure Service DTOs
export interface InfraServiceDto {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
}

export interface CreateInfraServiceRequest {
  name: string;
  description?: string;
}

export interface UpdateInfraServiceRequest {
  name: string;
  description?: string;
}

// Invoice DTOs
export interface InvoiceDto {
  id: string;
  serviceId: string;
  serviceName: string;
  amount: number;
  notes?: string;
  paymentStatus: number;
  invoiceFileUrl?: string;
  billingMonth: number;
  billingYear: number;
  expirationDate?: string;
  createdAt: string;
}

export interface CreateInvoiceRequest {
  serviceId: string;
  amount: number;
  notes?: string;
  paymentStatus: number;
  billingMonth: number;
  billingYear: number;
  expirationDate?: string;
}

export interface UpdateInvoiceRequest {
  amount: number;
  notes?: string;
  paymentStatus: number;
  expirationDate?: string;
}

export interface InvoiceSummaryDto {
  totalAmount: number;
  invoiceCount: number;
  paidCount: number;
  billingMonth: number;
  billingYear: number;
}

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  private readonly http = inject(HttpClient);
  private readonly serviceApiUrl = `${environment.apiUrl}/infra-services`;
  private readonly invoiceApiUrl = `${environment.apiUrl}/invoices`;

  getInvoiceApiUrl(): string {
    return this.invoiceApiUrl;
  }

  // --- Infrastructure Services ---

  getServices(): Observable<InfraServiceDto[]> {
    return this.http.get<InfraServiceDto[]>(this.serviceApiUrl);
  }

  createService(request: CreateInfraServiceRequest): Observable<InfraServiceDto> {
    return this.http.post<InfraServiceDto>(this.serviceApiUrl, request);
  }

  updateService(id: string, request: UpdateInfraServiceRequest): Observable<InfraServiceDto> {
    return this.http.put<InfraServiceDto>(`${this.serviceApiUrl}/${id}`, request);
  }

  deleteService(id: string): Observable<any> {
    return this.http.delete(`${this.serviceApiUrl}/${id}`);
  }

  // --- Invoices ---

  getInvoices(month: number, year: number): Observable<InvoiceDto[]> {
    return this.http.get<InvoiceDto[]>(this.invoiceApiUrl, {
      params: { month: month.toString(), year: year.toString() }
    });
  }

  getSummary(month: number, year: number): Observable<InvoiceSummaryDto> {
    return this.http.get<InvoiceSummaryDto>(`${this.invoiceApiUrl}/summary`, {
      params: { month: month.toString(), year: year.toString() }
    });
  }

  createInvoice(request: CreateInvoiceRequest): Observable<InvoiceDto> {
    return this.http.post<InvoiceDto>(this.invoiceApiUrl, request);
  }

  updateInvoice(id: string, request: UpdateInvoiceRequest): Observable<InvoiceDto> {
    return this.http.put<InvoiceDto>(`${this.invoiceApiUrl}/${id}`, request);
  }

  deleteInvoice(id: string): Observable<any> {
    return this.http.delete(`${this.invoiceApiUrl}/${id}`);
  }

  uploadInvoiceFile(invoiceId: string, file: File): Observable<{ invoiceFileUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ invoiceFileUrl: string }>(`${this.invoiceApiUrl}/${invoiceId}/upload`, formData);
  }

  getExpirationTracking(): Observable<ServiceExpirationDto[]> {
    return this.http.get<ServiceExpirationDto[]>(`${this.invoiceApiUrl}/expiration-tracking`);
  }

  getDefaultBillingPeriod(): Observable<DefaultBillingPeriodDto> {
    return this.http.get<DefaultBillingPeriodDto>(`${this.invoiceApiUrl}/default-period`);
  }

  getUnpaidCount(): Observable<number> {
    return this.http.get<number>(`${this.invoiceApiUrl}/unpaid-count`);
  }
}

export interface DefaultBillingPeriodDto {
  month: number;
  year: number;
  hasUnpaidInvoices: boolean;
}

export interface ServiceExpirationDto {
  serviceName: string;
  expirationDate?: string;
  daysRemaining?: number;
  billingMonth: number;
  billingYear: number;
}
